import { cn } from '@repo/ui';
import type { LucideIcon } from 'lucide-react';
import type { ReactElement } from 'react';

interface IconToolButtonProps {
  readonly icon: LucideIcon;
  /** 아이콘 아래 작은 이름. 버튼의 접근 이름이기도 하다. */
  readonly label: string;
  /** 켜진 도구인가. 없으면 aria-pressed 를 달지 않는다(한 번 누르는 동작 버튼). */
  readonly pressed?: boolean;
  readonly disabled?: boolean;
  /** 마우스를 올리면 보이는 설명(꺼진 사유 등). */
  readonly title?: string;
  readonly onClick?: () => void;
}

/**
 * 고객 에디터 도구 줄과 같은 모양의 작은 도구 버튼 — 아이콘 위, 작은 글씨 아래, 흐린 색.
 * 켜진 도구는 진한 바탕으로 보인다.
 */
export const IconToolButton = ({
  icon: Icon,
  label,
  pressed,
  disabled = false,
  title,
  onClick,
}: IconToolButtonProps): ReactElement => (
  <button
    type="button"
    aria-pressed={pressed}
    disabled={disabled}
    title={title}
    onClick={onClick}
    className={cn(
      'inline-flex h-12 min-w-12 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded px-1.5 text-muted-foreground transition-colors',
      'hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      'aria-pressed:bg-primary aria-pressed:text-primary-foreground',
      'disabled:cursor-not-allowed disabled:text-muted-foreground/40 disabled:hover:bg-transparent',
    )}
  >
    <Icon aria-hidden className="size-[18px] shrink-0" strokeWidth={1.5} />
    <span className="text-[11px] leading-none whitespace-nowrap">{label}</span>
  </button>
);
